import classNames from "classnames";
import React, { FunctionComponent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { PageName } from "../../../../types/customization/configuration";
import { TemplateType } from "../../../../types/customization/template";
import { countPages } from "../../../../utils/customization/configuration";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import "./style.scss";

interface Props {
  count: number;
  increment: number;
  max: number | undefined;
  min: number | undefined;
  pageName: PageName | undefined;
  type: TemplateType;
}

export const PagesSummary: FunctionComponent<Props> = ({
  count,
  increment,
  max,
  min = 1,
  pageName,
  type,
}) => {
  const intl = useIntl();
  let labelIntlId;
  if (type === "book" || type === "calendar" || type === "mural" || type === "print") {
    labelIntlId = `kustom_PagesSummary_label__${type}`;
  } else {
    const exhaustiveCheck: never = type;
    throw new Error(`Unsupported type ${exhaustiveCheck as string}`);
  }
  const showCountOver = type === "print" && (count <= min || increment > 1);
  const countOver = count <= min ? min : min + Math.ceil((count - min) / increment) * increment;

  const pageCount = (
    <span>
      <span className="PagesSummary-count">{intl.formatNumber(count)}</span>
      {showCountOver ? (
        <span className="PagesSummary-over">/{intl.formatNumber(countOver)}</span>
      ) : null}
    </span>
  );

  const pageNameIntlValue =
    pageName?.singular && pageName?.plural
      ? count > 1
        ? pageName.plural
        : pageName.singular
      : undefined;

  return (
    <div
      className={classNames("PagesSummary", {
        "PagesSummary--tooMuch": max && count > max,
      })}
    >
      <span className="PagesSummary-label">
        {pageNameIntlValue ? (
          <FormattedMessage
            id="kustom_PagesSummary_label__generic"
            values={{ pageCount, pageName: pageNameIntlValue }}
          />
        ) : (
          <FormattedMessage id={labelIntlId} values={{ pageCount, count }} />
        )}
      </span>
    </div>
  );
};

const PagesSummaryContainer: FunctionComponent = () => {
  const { content, configuration, template } = useCustomizationContext();
  const noCoverPageDefintions = getNoCoverDefinitions(template.pageDefinitions);

  return (
    <PagesSummary
      count={countPages(content, noCoverPageDefintions)}
      increment={(configuration && configuration.pages && configuration.pages.increment) || 1}
      max={configuration && configuration.pages && configuration.pages.max}
      min={configuration && configuration.pages && configuration.pages.min}
      pageName={configuration?.pageName}
      type={content.templateType}
    />
  );
};

export default PagesSummaryContainer;
